import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";

// urlBase64ToUint8Array — converts VAPID public key string to the format
// the browser Push API wants (applicationServerKey).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

/**
 * usePushNotifications — hook that manages the browser Push API subscription.
 *
 * Returns {
 *   supported, permission, subscribed, promptable,
 *   subscribe(), unsubscribe()
 * }
 *
 * Supported means: service worker + PushManager + Notification exist.
 * Promptable means: supported AND permission === "default".
 */
export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sup = typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window;
    setSupported(sup);
    if (sup) {
      setPermission(Notification.permission);
      // Check if we already have a subscription
      navigator.serviceWorker.ready.then(async (reg) => {
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      }).catch(() => { /* SW not registered yet */ });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || busy) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      // Ask for permission if not already granted
      if (Notification.permission === "default") {
        const p = await Notification.requestPermission();
        setPermission(p);
        if (p !== "granted") return { ok: false, reason: "denied" };
      } else if (Notification.permission === "denied") {
        return { ok: false, reason: "denied" };
      }

      // `navigator.serviceWorker.ready` never resolves if the SW didn't
      // register (bad MIME, cached bad version, iOS Safari without PWA
      // install). Race it against an 8s timeout so the button doesn't get
      // stuck on "Enabling…" — surface a clear error to the user instead.
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error("Service worker not ready (8s timeout)")), 8000)),
      ]);
      // Grab the server's public VAPID key
      const { data } = await http.get("/push/vapid-public-key");
      if (!data?.public_key || !data?.configured) {
        return { ok: false, reason: "server-not-configured" };
      }
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      // Send subscription to backend
      const subJson = sub.toJSON();
      await http.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      // Common causes on iOS: PWA not installed to home screen, SW cache
      // corrupt, or Notification.requestPermission rejected silently. Always
      // return with an error string so the UI can render it.
      const errStr = String(e?.message || e || "unknown error");
      console.warn("[push] subscribe failed:", errStr);
      return { ok: false, reason: "error", error: errStr };
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const unsubscribe = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await http.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys });
        } catch (e) { /* best-effort — the browser unsub below still frees the slot */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally { setBusy(false); }
  }, [supported, busy]);

  return {
    supported,
    permission,
    subscribed,
    busy,
    promptable: supported && permission === "default",
    subscribe,
    unsubscribe,
  };
}
